import { Navigate, Outlet, useLocation, useOutletContext } from "react-router-dom";
import { useEffect } from "react";
import { AppNavbar } from "./AppNavbar.js";
import { LoadingSpinner } from "./LoadingSpinner.js";
import { useSession, type SessionUser } from "../context/SessionContext.js";
import { useTheme } from "../context/ThemeContext.js";
import { useFeatureFlags } from "../hooks/useFeatureFlags.js";
import { resolveShell } from "../lib/shell.js";
import { resolveQuickLinks } from "../lib/quickLinks.js";

export interface AuthenticatedContext {
  agent: SessionUser;
}

export function useAuthenticatedAgent() {
  return useOutletContext<AuthenticatedContext>().agent;
}

function navbarProps(
  agent: SessionUser,
  shell: ReturnType<typeof resolveShell>,
  logout: () => void,
  contractsEnabled: boolean
) {
  return {
    agentEmail: agent.email,
    organizationName: agent.organization?.brandName ?? "Altura",
    agentRole: agent.role,
    isStaff: agent.isStaff ?? true,
    shell,
    contractsEnabled,
    downlinesEnabled: Boolean(agent.canManageDownlines),
    // Agent-facing tooling — a provider (PPG) workspace has no use for it.
    quickLinks: shell === "provider" ? [] : resolveQuickLinks(agent.organization),
    onSignOut: logout,
  };
}

/** Persistent shell for authenticated routes — navbar stays mounted across navigations. */
export function AuthenticatedLayout() {
  const { agent, loading, logout, stopImpersonating } = useSession();
  const { flags } = useFeatureFlags();
  const { setShell } = useTheme();
  const location = useLocation();

  const shell = agent ? resolveShell(agent) : "staff";

  useEffect(() => {
    if (agent) setShell(shell);
  }, [agent, shell, setShell]);

  if (loading) return <LoadingSpinner />;

  if (!agent) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }

  return (
    <div className="min-h-screen bg-surface-page">
      <AppNavbar {...navbarProps(agent, shell, logout, flags.contracts)} />
      <div className="app-shell-content min-h-screen">
        {agent.impersonation && (
          <div className="border-b border-status-nigo-border bg-status-nigo-bg px-4 py-2 text-sm text-status-nigo-text">
            <div className="mx-auto flex max-w-shell flex-wrap items-center justify-between gap-2 px-4 sm:px-6">
              <p>
                Viewing as <span className="font-medium text-content-primary">{agent.email}</span>
                {" · "}
                signed in as admin {agent.impersonation.email}
              </p>
              <button
                type="button"
                onClick={() => void stopImpersonating()}
                className="focus-ring rounded-lg border border-status-nigo-border bg-surface-raised px-3 py-1 text-xs font-semibold text-content-primary transition-colors hover:bg-surface-overlay"
              >
                Exit impersonation
              </button>
            </div>
          </div>
        )}
        <Outlet context={{ agent }} />
      </div>
    </div>
  );
}
